import { useState, useEffect } from 'react';

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const media = window.matchMedia(query);
      
      // Set matches to current media match state
      const updateMatches = () => {
        setMatches(media.matches);
      };
      
      // Call handler right away to set initial state
      updateMatches();
      
      // Set up event listener
      media.addEventListener('change', updateMatches);
      
      // Clean up on unmount
      return () => {
        media.removeEventListener('change', updateMatches);
      };
    }
  }, [query]);

  return matches;
}

export function useIsMobile() {
  return useMediaQuery('(max-width: 640px)');
}

export function useIsTablet() {
  return useMediaQuery('(min-width: 641px) and (max-width: 1024px)');
}

export function useIsDesktop() {
  return useMediaQuery('(min-width: 1025px)');
}

export function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check if device is touch capable
      const isTouchDevice = (
        'ontouchstart' in window || 
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
      
      setIsTouch(isTouchDevice);
    }
  }, []);
  
  return isTouch;
}

export function useDeviceOrientation() {
  const [orientation, setOrientation] = useState('portrait');
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const updateOrientation = () => {
        if (window.matchMedia("(orientation: portrait)").matches) {
          setOrientation('portrait');
        } else {
          setOrientation('landscape');
        }
      };
      
      // Call handler right away to set initial state
      updateOrientation();
      
      // Set up event listeners
      window.addEventListener('resize', updateOrientation);
      window.addEventListener('orientationchange', updateOrientation);
      
      // Clean up on unmount
      return () => {
        window.removeEventListener('resize', updateOrientation);
        window.removeEventListener('orientationchange', updateOrientation);
      };
    }
  }, []);
  
  return orientation;
}

export default useMediaQuery; 